import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ApiRequestError } from '@/lib/api';
import { useToast } from '@/components/ui';
import { AMBIENT_SPOT_LABEL } from '@/features/spots/current';
import { GlowLocationError } from './glow-context-def';
import { useGlow } from './use-glow';

/** A tiny bulb that drains over the Glow lifetime. */
function GlowBulb({ expiresAt, className }: { expiresAt: string | null; className?: string }) {
  const pct = useMemo(() => {
    if (!expiresAt) return 1;
    const left = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.min(1, left / (60 * 60_000)));
  }, [expiresAt]);
  return (
    <span
      className={`relative inline-block h-4 w-3 overflow-hidden rounded-full border-2 border-ink ${className ?? ''}`}
      aria-hidden
    >
      <span
        className="absolute inset-x-0 bottom-0 bg-u transition-[height] duration-1000 ease-linear"
        style={{ height: `${pct * 100}%` }}
      />
    </span>
  );
}

/**
 * The Glow toggle, in two sizes sharing one brain (`useGlow`, mounted once by
 * `AppShell`'s `GlowProvider`): `compact` sits in the app header on every
 * `/app/*` page so Glow's state is visible the instant you land in the app,
 * full sits atop the Radar scope. Tapping ON with nowhere checked in yet
 * silently resolves (or forms) a nearby Spot first.
 */
export function GlowPill({ compact = false }: { compact?: boolean }) {
  const { glowOn, glowExpiresAt, resolving, spotName, locationBlocked, toggleGlow } = useGlow();
  const { toast } = useToast();
  const reduce = useReducedMotion();
  const [bloomKey, setBloomKey] = useState(0);
  const wasOn = useRef(glowOn);

  useEffect(() => {
    if (glowOn && !wasOn.current) setBloomKey((k) => k + 1); // one-shot "lights up" ripple
    wasOn.current = glowOn;
  }, [glowOn]);

  const countdown = useMemo(() => {
    if (!glowOn || !glowExpiresAt) return null;
    const mins = Math.max(0, Math.round((new Date(glowExpiresAt).getTime() - Date.now()) / 60000));
    return `${mins} min left`;
  }, [glowOn, glowExpiresAt]);

  const onToggle = async () => {
    try {
      await toggleGlow();
    } catch (e) {
      toast(
        e instanceof ApiRequestError || e instanceof GlowLocationError
          ? e.message
          : "Couldn't find you a Spot — try again.",
        'critical',
      );
    }
  };

  if (compact) {
    return (
      <button
        onClick={onToggle}
        disabled={resolving}
        aria-pressed={glowOn}
        className={`flex items-center gap-1.5 rounded-pill border-2 border-ink px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${glowOn ? 'bg-u-soft' : 'bg-surface'}`}
        title={
          resolving
            ? 'Finding you…'
            : glowOn
              ? (countdown ?? "you're glowing")
              : 'Glow is off — tap to be seen'
        }
      >
        <span
          className={`inline-block size-2 rounded-full ${glowOn ? 'bg-u' : 'bg-ink-faint'}`}
          aria-hidden
        />
        {resolving ? 'Finding you…' : glowOn ? 'Glowing ✨' : 'Glow off'}
      </button>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-tile border-2 border-ink p-4 transition-colors ${glowOn ? 'bg-u-soft' : ''}`}
    >
      <AnimatePresence>
        {glowOn && !reduce && (
          <motion.span
            key={bloomKey}
            className="pointer-events-none absolute right-6 top-1/2 z-0 size-6 -translate-y-1/2 rounded-full bg-u"
            initial={{ scale: 0, opacity: 0.7 }}
            animate={{ scale: 26, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>
      <div className="relative flex items-center justify-between">
        <div>
          <p className="flex items-center gap-1.5 font-display font-semibold">
            {resolving ? 'Finding you…' : glowOn ? 'You’re glowing ✨' : 'Glow is off'}
            {glowOn && countdown && <GlowBulb expiresAt={glowExpiresAt} />}
          </p>
          <p className="text-xs text-ink-soft">
            {resolving
              ? 'Placing you on the map'
              : glowOn
                ? (countdown ??
                  (spotName && spotName !== AMBIENT_SPOT_LABEL
                    ? `visible at ${spotName}`
                    : 'visible to people nearby'))
                : 'nobody can see or Spark you'}
          </p>
        </div>
        <button
          onClick={onToggle}
          disabled={resolving}
          aria-pressed={glowOn}
          className={`relative z-10 h-8 w-14 rounded-pill border-2 border-ink transition-colors disabled:opacity-60 ${glowOn ? 'bg-u' : 'bg-surface-sunken'}`}
        >
          <motion.span
            layout
            transition={{ type: 'spring', stiffness: 500, damping: 32 }}
            className={`absolute top-0.5 size-6 rounded-full border-2 border-ink bg-surface ${glowOn ? 'left-6' : 'left-0.5'}`}
          />
        </button>
      </div>
      {locationBlocked && (
        <p className="relative mt-2 text-xs text-critical">
          Location is blocked — enable it in site settings.
        </p>
      )}
    </div>
  );
}
